import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, fmtDateTime } from '@rydres/ui';
import { riderNotifications, sitePath, type RiderNotification } from '../lib/passenger.js';
import { Shell, useRiderGate } from './Passenger.js';

/** The bell on Home, with how many updates are new. */
export function Bell() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => riderNotifications.unread().then((r) => setN(r.unread), () => undefined);
    void load();
    const timer = setInterval(() => document.visibilityState === 'visible' && void load(), 60_000);
    return () => clearInterval(timer);
  }, []);
  return (
    <Link to="/me/updates" aria-label={n ? `Updates, ${n} new` : 'Updates'} className="relative flex h-11 w-11 items-center justify-center rounded-full bg-card2 text-ink">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 8a6 6 0 1112 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a2 2 0 003.4 0" />
      </svg>
      {n > 0 && <span className="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] font-black leading-4 text-card">{n > 99 ? '99+' : n}</span>}
    </Link>
  );
}

/** rydres.com/me/updates: every update about the rider's rides; opening it marks them seen. */
export function UpdatesPage() {
  const { p, gate } = useRiderGate('Updates', 'Sign in with the mobile number you book with to see updates about your rides.');
  const [list, setList] = useState<RiderNotification[] | null>(null);
  const signedIn = Boolean(p.me);
  useEffect(() => {
    if (!signedIn) return;
    riderNotifications.list().then(
      (r) => {
        setList(r.notifications);
        if (r.unread) void riderNotifications.read().catch(() => undefined);
      },
      () => setList([]),
    );
  }, [signedIn]);
  if (gate) return gate;
  return (
    <Shell>
      <h1 className="text-2xl font-black">Updates</h1>
      {list === null ? (
        <Spinner />
      ) : list.length === 0 ? (
        <p className="text-sm text-ink2">Nothing yet. Updates about your rides show up here.</p>
      ) : (
        <ul className="space-y-2">
          {list.map((n) => {
            const body = (
              <>
                <div className="flex items-baseline justify-between gap-3">
                  <span className={n.read ? 'font-bold' : 'font-black'}>{n.title}</span>
                  <span className="shrink-0 text-xs text-muted">{fmtDateTime(n.at)}</span>
                </div>
                <p className="mt-0.5 text-sm text-ink2">{n.body}</p>
              </>
            );
            const to = sitePath(n.url);
            return (
              <li key={n.id}>
                {to ? <Link to={to} className={`block rounded-2xl border bg-card p-3 ${n.read ? 'border-line' : 'border-accent'}`}>{body}</Link> : <div className="rounded-2xl border border-line bg-card p-3">{body}</div>}
              </li>
            );
          })}
        </ul>
      )}
    </Shell>
  );
}
